import React, { useEffect } from "react";
import { Row, Col } from "reactstrap";
import { useFormik } from "formik";
import { levelSchema, levelDefaults } from "../../../../models/level";
import PropTypes from "prop-types";
import { OverlayLoader, NeoCard, NeoInput, NeoButton, NeoBadge } from "../../../../components/Common";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";

/**
 * LevelEditor
 * Editor completo: Nome e Valor do nível.
 * O campo "order" é gerenciado pelo drag and drop e salvo automaticamente.
 */
const LevelEditor = ({ level, onSave, onDelete, isSaving, onChange }) => {

    const formik = useFormik({
        initialValues: levelDefaults,
        validationSchema: levelSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    // Sincroniza estado quando o nível selecionado muda
    useEffect(() => {
        if (level) {
            formik.resetForm({
                values: {
                    id: level.id || "",
                    name: level.name || "",
                    value: level.value || 0,
                    order: level.order || 0,
                    status: level.status || GLOBAL_STATUS.ACTIVE,
                    createdAt: level.createdAt || null,
                    createdBy: level.createdBy || null
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [level]);

    // Notifica mudança para o pai (isDirty)
    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    if (!level) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-stairs"></i>
                        </div>
                    </div>
                    <h5>Selecione um nível</h5>
                    <p className="text-muted">Escolha um nível na lista ao lado ou crie uma nova para gerenciar.</p>
                </div>
            </div>
        );
    }

    return (
        <NeoCard
            title={
                <span className="d-flex align-items-center gap-2">
                    <span className="text-truncate">{formik.values.id ? formik.values.name : "Novo Nível"}</span>
                    {formik.values.id && (
                        <NeoBadge color={formik.values.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'}>
                            {GLOBAL_STATUS_LABELS[formik.values.status] || 'Desconhecido'}
                        </NeoBadge>
                    )}
                </span>
            }
            subtitle="Dados do nível"
            headerIcon="mdi-stairs"
            headerActions={
                <div className="d-flex gap-2 flex-shrink-0">
                    {formik.values.id && (
                        <NeoButton
                            variant="soft-danger"
                            square={true}
                            onClick={() => onDelete(formik.values.id)}
                            disabled={isSaving}
                            title="Excluir Nível"
                            icon="mdi-trash-can-outline"
                        />
                    )}
                    <NeoButton
                        variant="primary"
                        onClick={formik.handleSubmit}
                        loading={isSaving}
                        loadingText="Salvando..."
                    >
                        Salvar
                    </NeoButton>
                </div>
            }
            className="overflow-hidden rounded-0 position-relative"
            bodyClassName="p-4"
            noBorder={true}
        >
            <OverlayLoader show={isSaving} label="Salvando..." />
            <Row>
                <Col lg={12}>
                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                        <i className="mdi mdi-information-outline me-2 font-size-16 text-primary"></i>
                        Dados do Nível
                    </h6>
                </Col>

                <Col md="6">
                    <NeoInput
                        label="Nome do Nível"
                        type="text"
                        placeholder="Ex: Iniciante, Intermediário, Avançado..."
                        flat={true}
                        error={formik.touched.name && formik.errors.name ? formik.errors.name : ""}
                        {...formik.getFieldProps("name")}
                    />
                </Col>
                <Col md="3">
                    <NeoInput
                        label="Valor (Pts)"
                        type="number"
                        placeholder="0"
                        flat={true}
                        error={formik.touched.value && formik.errors.value ? formik.errors.value : ""}
                        {...formik.getFieldProps("value")}
                    />
                </Col>
                <Col md="3">
                    <div className="neo-input-wrapper neo-input-wrapper-md">
                        <label className="neo-input-label">Status</label>
                        <div className="form-check form-switch pt-2">
                            <input
                                type="checkbox"
                                role="switch"
                                checked={formik.values.status === GLOBAL_STATUS.ACTIVE}
                                onChange={(e) => {
                                    formik.setFieldValue("status", e.target.checked ? GLOBAL_STATUS.ACTIVE : GLOBAL_STATUS.INACTIVE);
                                    onChange(true);
                                }}
                                className="form-check-input custom-switch-primary cursor-pointer"
                                id="level-status-switch"
                            />
                            <label className="form-check-label ms-2 small text-muted cursor-pointer" htmlFor="level-status-switch">
                                {GLOBAL_STATUS_LABELS[formik.values.status] || formik.values.status}
                            </label>
                        </div>
                    </div>
                </Col>
            </Row>
        </NeoCard>
    );
};

LevelEditor.propTypes = {
    level: PropTypes.object,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default LevelEditor;
